import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { RadarObject } from '../types';

/**
 * スキルパラメーター 5軸レーダーチャート（自前 SVG・チャートライブラリ未導入）。
 *
 * 軸は正準順（付録D.1 / 設計書 §5）で固定:
 *   interpersonal(対人) / leadership(統率) / discipline(規律) / knowledge(知識) / execution(実行)
 *
 * - 値域 0-100。各軸は 12時方向（上）から時計回りに配置。
 * - `is_provisional=true`（新人＝判定<3、全軸55の暫定円）は中立色（グレー）で描画し、
 *   確定値（青：付録C.4）と視覚的に区別する。
 *
 * 色（付録C.4）:
 *   塗り（半透明ライトブルー）#B3D5F7 / 枠線 #0071E3 / グリッド線 #E0E0E0
 */

interface SkillRadarChartProps {
  /** 正準順の5軸値（0-100）。RadarObject をそのまま渡せる。 */
  values: Pick<
    RadarObject,
    'interpersonal' | 'leadership' | 'discipline' | 'knowledge' | 'execution'
  >;
  /** 暫定（新人）か。true で中立色（グレー）描画。 */
  is_provisional?: boolean;
  /** SVG の一辺サイズ（px）。 */
  size?: number;
}

// 正準軸順（順序を変えない＝サーバ契約と一致させる）
const AXES: { key: keyof SkillRadarChartProps['values']; label: string }[] = [
  { key: 'interpersonal', label: '対人' },
  { key: 'leadership', label: '統率' },
  { key: 'discipline', label: '規律' },
  { key: 'knowledge', label: '知識' },
  { key: 'execution', label: '実行' },
];

// 確定値の色（付録C.4）
const FILL_COLOR = '#B3D5F7';
const STROKE_COLOR = '#0071E3';
const GRID_COLOR = '#E0E0E0';
// 暫定（新人）の中立色
const PROVISIONAL_FILL = '#E0E0E0';
const PROVISIONAL_STROKE = '#BDBDBD';

const SkillRadarChart: React.FC<SkillRadarChartProps> = ({
  values,
  is_provisional = false,
  size = 240,
}) => {
  const cx = size / 2;
  const cy = size / 2;
  // ラベル分の余白を確保（外周ラベルがはみ出さないよう半径を抑える）
  const radius = size * 0.34;
  const axisCount = AXES.length;

  // 0-100 を 0-1 に正規化（範囲外はクランプ）
  const normalize = (v: number): number => Math.max(0, Math.min(100, v)) / 100;

  // i 番目の軸の角度（12時方向＝-90度から時計回り）
  const angleFor = (i: number): number => (Math.PI * 2 * i) / axisCount - Math.PI / 2;

  // 半径 r（0-1）・軸 i の座標
  const pointAt = (i: number, r: number): { x: number; y: number } => {
    const angle = angleFor(i);
    return {
      x: cx + Math.cos(angle) * radius * r,
      y: cy + Math.sin(angle) * radius * r,
    };
  };

  // グリッド（同心多角形）の比率
  const gridLevels = [0.25, 0.5, 0.75, 1];

  // データ多角形の頂点
  const dataPoints = AXES.map((axis, i) => pointAt(i, normalize(values[axis.key])));
  const dataPolygon = dataPoints.map((p) => `${p.x},${p.y}`).join(' ');

  const fillColor = is_provisional ? PROVISIONAL_FILL : FILL_COLOR;
  const strokeColor = is_provisional ? PROVISIONAL_STROKE : STROKE_COLOR;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Box
        component="svg"
        viewBox={`0 0 ${size} ${size}`}
        sx={{ width: '100%', maxWidth: size, height: 'auto', display: 'block' }}
        role="img"
        aria-label="スキルパラメーター レーダーチャート"
      >
        {/* グリッド（同心多角形） */}
        {gridLevels.map((level) => {
          const pts = AXES.map((_, i) => {
            const p = pointAt(i, level);
            return `${p.x},${p.y}`;
          }).join(' ');
          return (
            <polygon
              key={`grid-${level}`}
              points={pts}
              fill="none"
              stroke={GRID_COLOR}
              strokeWidth={1}
            />
          );
        })}

        {/* 軸線（中心から各頂点へ） */}
        {AXES.map((_, i) => {
          const outer = pointAt(i, 1);
          return (
            <line
              key={`axis-${i}`}
              x1={cx}
              y1={cy}
              x2={outer.x}
              y2={outer.y}
              stroke={GRID_COLOR}
              strokeWidth={1}
            />
          );
        })}

        {/* データ多角形 */}
        <polygon
          points={dataPolygon}
          fill={fillColor}
          fillOpacity={0.5}
          stroke={strokeColor}
          strokeWidth={2}
          strokeLinejoin="round"
        />

        {/* データ頂点 */}
        {dataPoints.map((p, i) => (
          <circle key={`dot-${i}`} cx={p.x} cy={p.y} r={3} fill={strokeColor} />
        ))}

        {/* 軸ラベル */}
        {AXES.map((axis, i) => {
          const labelPos = pointAt(i, 1.22);
          // 上下に応じてアンカーを調整（端のラベルがはみ出さないように）
          const anchor =
            Math.abs(labelPos.x - cx) < 1 ? 'middle' : labelPos.x > cx ? 'start' : 'end';
          return (
            <text
              key={`label-${i}`}
              x={labelPos.x}
              y={labelPos.y}
              textAnchor={anchor}
              dominantBaseline="middle"
              fontSize={size * 0.055}
              fill="#3c3c3c"
              fontWeight={600}
            >
              {axis.label}
            </text>
          );
        })}
      </Box>

      {is_provisional && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, textAlign: 'center' }}>
          勤務実績がまだ少ないため、暫定値を表示しています
        </Typography>
      )}
    </Box>
  );
};

export default SkillRadarChart;
